// Protocol limits and default simulation values (architecture §2).
//
// The `*_DEFAULTS` objects mirror the matching sections of config/default.json exactly; the server
// loads the real values from config, and these defaults are what the shared formulas fall back
// to when called without parameters. `test/constants.test.ts` fails if the two drift apart
// (architecture §8).

/** Deeply read-only view of a plain data object. */
export type DeepReadonly<T> = T extends (infer U)[]
  ? readonly DeepReadonly<U>[]
  : T extends object
    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
    : T;

/** Freezes a plain data object and everything in it; returns it typed read-only. */
export function deepFreeze<T>(value: T): DeepReadonly<T> {
  if (value !== null && typeof value === 'object') {
    for (const v of Object.values(value)) deepFreeze(v);
    Object.freeze(value);
  }
  return value as DeepReadonly<T>;
}

// ---------------------------------------------------------------------------------------------
// Simulation defaults: config/default.json "sim" (mechanics Appendix A).
// ---------------------------------------------------------------------------------------------

export const SIM_DEFAULTS = deepFreeze({
  tickRate: 25,
  maxCatchUpTicks: 3,
  seed: null as number | null,
  world: { size: 14142.136, borderOverhang: 0.5 },
  mass: { start: 10, min: 10, maxPerCell: 22500 },
  cells: { max: 16 },
  speed: { coef: 1930, exponent: -0.439 },
  steer: { gain: 9, velTauSec: 0.07, minStepU: 0.05 },
  input: { targetMargin: 2000 },
  eat: { radiusRatio: 1.15, overlapDivisor: 3 },
  antiFeeder: { enabled: false, eaterMinMass: 625, preyMaxMass: 17 },
  boost: { retainPer40ms: 0.9, maxStepPer40ms: 78, endBelow: 1 },
  split: { minMass: 36, boost: 780, spawnOffset: 40, noCollideSec: 0.52, queueMax: 4 },
  merge: { baseSec: 30, perRadiusSec: 0.2 },
  eject: {
    minMass: 35,
    cost: 18,
    pelletMass: 14.4,
    boost: 780,
    jitterRad: 0.3,
    cooldownSec: 0.08,
    maxLive: 2000,
  },
  food: {
    target: 4000,
    spawnMass: 1,
    maxMass: 5,
    growEverySec: 60,
    spawnPerTick: 10,
    avoidViruses: false,
  },
  virus: {
    mass: 100,
    feedsToShoot: 7,
    shotBoost: 780,
    min: 50,
    max: 100,
    giveMass: true,
    breed: true,
    pushPerFeed: 0,
  },
  pop: { minPiece: 19.36 },
  decay: { ratePerSec: 0.002, minMass: 10 },
  spawn: { tries: 64, safeMargin: 50, threatMargin: 1500, fromEjectedChance: 0.5 },
  disconnect: { lingerSec: 60 },
  nick: { maxLength: 15 },
  view: {
    baseW: 1920,
    baseH: 1080,
    refRadius: 64,
    exponent: 0.4,
    minScale: 0.15,
    margin: 100,
    minWheelZoom: 0.8,
  },
  spectate: { freeScale: 0.4, freeSpeed: 800 },
  leaderboard: { size: 10, everyTicks: 25 },
  minimap: { everyTicks: 5, top: 20 },
  antiTeam: { enabled: true, windowSec: 60, threshold: 3, maxMult: 5 },
});

export type SimDefaults = typeof SIM_DEFAULTS;

/** config/default.json "room". */
export const ROOM_DEFAULTS = deepFreeze({
  maxPlayers: 100,
  maxSpectators: 50,
  maxMenuViewers: 200,
  menuIdlePauseSec: 300,
});

/** config/default.json "bots" (mechanics §16). */
export const BOT_DEFAULTS = deepFreeze({
  fillTo: 40,
  respawnSec: [1, 3] as [number, number],
  skillMix: [0.4, 0.4, 0.2] as [number, number, number],
  skinChance: 0.6,
  massSoftCap: 6000,
  softCapDecayMult: 4,
  splitCooldownTicks: 25,
  markOnLeaderboard: false,
  overfillSlack: 5,
  overfillGraceSec: 30,
});

/** config/default.json "modes": per-mode overrides and mode-only settings (mechanics §17). */
export const MODE_DEFAULTS = deepFreeze({
  ffa: {},
  party: {
    bots: { fillTo: 10 },
    room: { maxPlayers: 50 },
    sim: { antiTeam: { enabled: false } },
    emptyTtlSec: 120,
    codeLength: 6,
    createsPerIpPer10Min: 5,
  },
  teams: {
    sim: { virus: { giveMass: false }, spawn: { fromEjectedChance: 0 } },
    teamCount: 3,
    colorJitter: 24,
    rebalanceShare: 0.45,
  },
  experimental: {
    sim: {
      virus: { breed: false, pushPerFeed: 120 },
      food: { avoidViruses: true },
    },
    spawnerCount: 10,
    spawnerMass: 222.01,
    spawnerMaxMass: 100000,
    spawnerActivePerSec: 25,
    spawnerPassivePerSec: 0.625,
    spawnerPassiveMaxLive: 96,
    spawnerEmitBoostMin: 45,
    spawnerEmitBoostMax: 90,
    emittedFoodMax: 3000,
  },
  battleroyale: {
    room: { maxPlayers: 50 },
    bots: { fillTo: 0 },
    sim: {
      virus: { breed: false, pushPerFeed: 120 },
      food: { avoidViruses: true },
      spawn: { fromEjectedChance: 0 },
      antiTeam: { enabled: false },
    },
    minPlayers: 20,
    lobbyTimeoutSec: 60,
    countdownSec: 20,
    overSec: 10,
    zone: {
      startRadius: 10000,
      waits: [150, 90, 60, 60, 30],
      radii: [0.6, 0.35, 0.18, 0.06, 0],
      shrinkSec: [20, 20, 20, 20, 60],
    },
    outside: { damagePerSec: 10, damageFractionPerSec: 0.01, splitEverySec: 1 },
  },
});

/** config/default.json "net": connection limits and timeouts (protocol §1, §4, §6.5, §9.3). */
export const NET_DEFAULTS = deepFreeze({
  handshakeTimeoutSec: 5,
  idleTimeoutSec: 15,
  maxClientMessageBytes: 1024,
  frameBudgetBytes: 32768,
  keepMargin: 300,
  rateLimit: {
    burst: 40,
    refillPerSec: 60,
    playPerSec: 1,
    spectatePerSec: 4,
    specialPerSec: 4,
    optionsPerSec: 5,
    pingPerSec: 2,
    maxDrops: 200,
    dropWindowSec: 10,
    cashOutPerSec: 4,
    moneySplitPerSec: 8,
    moneyEjectPerSec: 15,
  },
  backpressure: { skipOptionalBytes: 262144, closeBytes: 1048576, closeAfterSec: 5 },
});

/**
 * config/default.json "money" (money-mode §10). The server reads the real values from config;
 * the client learns a room's values from WELCOME. Mirrored here for the constants test and for
 * shared helpers' defaults only. `enabled` is true in default.json (dev) and false in
 * production.json until the owner turns money rooms on.
 */
export const MONEY_DEFAULTS = deepFreeze({
  enabled: true,
  stakes: [1] as number[],
  rake: 0.1,
  minLifeSec: 10,
  cashOutHoldSec: 5,
  cashOutKey: 'Q',
  idleSec: 60,
  emptyRoomTtlMin: 10,
  labelCurrency: 'USD' as 'USD' | 'SOL',
  maxRoomsPerStake: 4,
  settlement: 'fake' as const,
  allowFakeSettlement: false,
  payoutTimeoutSec: 15,
  exitPayoutWaitSec: 3,
  price: { source: 'fake' as const, fakeSolUsd: 150, refreshSec: 30, maxAgeSec: 300 },
  room: { maxPlayers: 30 },
});

// ---------------------------------------------------------------------------------------------
// Fixed facts and client-side constants that are not config keys.
// ---------------------------------------------------------------------------------------------

/** Lamports per SOL (Solana's fixed unit). Money is held and settled in integer lamports. */
export const LAMPORTS_PER_SOL = 1_000_000_000;

/** The default map side (mechanics §1.1); counts that scale with area are relative to this. */
export const REFERENCE_WORLD_SIZE = 14142.136;

/** Radius per √mass (mechanics §3: r = 10·√m). */
export const RADIUS_PER_SQRT_MASS = 10;

/** Boost curves are defined per 40 ms of simulated time (mechanics §0.4). */
export const BOOST_REFERENCE_DT_SEC = 0.04;

/** Background grid spacing in world units (mechanics §1.3). */
export const GRID = deepFreeze({ minorU: 50, majorU: 250 });

/** Wheel zoom (mechanics §14.4). The minimum mirrors `sim.view.minWheelZoom`. */
export const WHEEL_ZOOM = deepFreeze({ min: 0.8, max: 4, step: 1.1, default: 1 });

/**
 * Client camera easing half-lives in ms (mechanics §14.3, movement.md §1.5–1.6: measured on agar,
 * position one third of the gap per 60 Hz frame, zoom τ ≈ 207 ms).
 */
export const CAMERA_EASE = deepFreeze({
  positionHalfLifeMs: 28,
  zoomHalfLifeMs: 145,
  observerHalfLifeMs: 500,
});

/** Bot skill levels (mechanics §16.3). `share` is the spawn probability (= `bots.skillMix`). */
export const BOT_SKILLS = deepFreeze({
  easy: {
    share: 0.4,
    thinkEveryTicks: 4,
    aimNoiseRad: 0.25,
    maxCellsForSplitKill: 1,
    threatMult: 0.7,
  },
  normal: {
    share: 0.4,
    thinkEveryTicks: 2,
    aimNoiseRad: 0.12,
    maxCellsForSplitKill: 2,
    threatMult: 1,
  },
  hard: {
    share: 0.2,
    thinkEveryTicks: 1,
    aimNoiseRad: 0.03,
    maxCellsForSplitKill: 4,
    threatMult: 1.3,
  },
});

export type BotSkill = keyof typeof BOT_SKILLS;
export const BOT_SKILL_IDS: readonly BotSkill[] = ['easy', 'normal', 'hard'];

/** Bot steering and split-kill numbers (mechanics §16.4–16.5). */
export const BOT_TUNING = deepFreeze({
  weights: {
    pellet: 1,
    ejected: 2,
    preyPerLogMass: 4,
    threatPerLogMass: -6,
    threatSplitKillFactor: 2,
    neutral: -0.5,
    virusWouldPop: -3,
    virusAtMaxCells: 4,
    virusHide: 1.5,
    spawnerThreat: -6,
    spawnerAtMaxCells: 3,
  },
  minEdgeDistanceU: 1,
  hideThreatRangeU: 600,
  /** Wall push with a threat within hideThreatRangeU: `wallWeight × (1 − dist/wallRangeU)`. */
  wallRangeU: 300,
  wallWeight: 2,
  /**
   * Wall push with no threat near (ball.io): short and weak, so nearby food wins and bots harvest
   * along the walls; it only keeps a wandering bot from scraping into a wall or a corner.
   */
  calmWallRangeU: 60,
  calmWallWeight: 0.1,
  aimDistanceU: 800,
  minForce: 0.01,
  wanderTurnRadPerSec: 0.5,
  wanderTurnEverySec: [3, 6] as [number, number],
  splitReachFactor: 0.85,
  splitThreatReachFactor: 1.5,
  splitLockTicks: 10,
  teamFeedRangeU: 400,
  teamFeedMassRatio: 2,
  teamFeedEverySec: 1,
  skinChance: 0.6,
});

// ---------------------------------------------------------------------------------------------
// Protocol limits (protocol.md).
// ---------------------------------------------------------------------------------------------

/** HELLO magic: ASCII "BALL" (protocol §5.1). */
export const HELLO_MAGIC: readonly number[] = Object.freeze([0x42, 0x41, 0x4c, 0x4c]);

/** Largest client message accepted, in bytes (protocol §1; `ws` maxPayload). */
export const MAX_CLIENT_MESSAGE_BYTES = NET_DEFAULTS.maxClientMessageBytes;

/** The server stops adding ADD records once a FRAME would pass this size (protocol §6.5). */
export const FRAME_BUDGET_BYTES = NET_DEFAULTS.frameBudgetBytes;

/** Longest legal `vu` (protocol §2): 5 bytes, maximum 2³² − 1. */
export const VARINT_MAX_BYTES = 5;
export const VARINT_MAX_VALUE = 0xffffffff;

/** Maximum UTF-8 byte length of each `str` field (protocol §5). */
export const STR_MAX_BYTES = deepFreeze({
  clientBuild: 32,
  nick: 64,
  skinId: 40,
  serverBuild: 32,
  roomId: 32,
  partyCode: 6,
  playerName: 64,
  leaderboardName: 64,
  standingName: 64,
  noticeArg: 64,
  errorMessage: 128,
  /** PLAY entry ticket id (money rooms). */
  ticketId: 64,
  /**
   * HELLO authToken (a Privy ES256 access token is about 400–600 bytes). Bounded so a HELLO with
   * a full clientBuild and token stays under MAX_CLIENT_MESSAGE_BYTES (1 + 4 + 2 + 33 + 2 + 900).
   */
  authToken: 900,
  /** WELCOME money block cashOutKey. */
  cashOutKey: 16,
});

/** Count limits of list fields (protocol §5). */
export const MAX_LEADERBOARD_ENTRIES = 10;
export const MAX_MINIMAP_ENTRIES = 70;
export const MAX_MASS_SAMPLES = 600;
export const MAX_STANDINGS = 10;
/** MONEY section records: at most one per player in a room (room.maxPlayers ≤ 1000). */
export const MAX_MONEY_ENTRIES = 1000;

/** OPTIONS carries the wheel zoom × 100 as a u16, limited to this range (protocol §5.1). */
export const WHEEL_ZOOM_WIRE_MIN = 80;
export const WHEEL_ZOOM_WIRE_MAX = 400;

/** Default quantisation steps announced in WELCOME (protocol §2). */
export const DEFAULT_POS_Q = 0.25;
export const DEFAULT_RAD_Q = 0.25;

/** Client-side network timing (protocol §3, §4, §5.1, §7, §8.4). */
export const NET_TIMING = deepFreeze({
  pingIntervalSec: 2,
  inputHeartbeatMs: 250,
  inputMinMoveU: 0.5,
  optionsMaxPerSec: 5,
  connectAttemptTimeoutSec: 5,
  retryWaitsSec: [0.25, 0.5, 1, 2, 4],
  retryJitter: 0.2,
  shutdownFirstRetrySec: 2,
  infoPollSec: 30,
  /** The menu re-asks /api/info on opening (Esc, death) when its copy is older than this. */
  infoFreshSec: 15,
  stalledFrameMs: 250,
  playTimeoutSec: 3,
});

/** Client interpolation (protocol §8). */
export const INTERPOLATION = deepFreeze({
  historySamples: 4,
  /** movement.md §4.5 P3: 60 once rate estimation and bounded extrapolation are in (they are). */
  delayMinMs: 60,
  delayMaxMs: 160,
  jitterWindowFrames: 50,
  offsetDriftPerFrame: 0.01,
  delaySlewMsPerFrame: 2,
  eatenEffectMs: 100,
  foodPopInMs: 150,
  pingEmaKeep: 0.8,
});

/** Per-life mass history (mechanics §13.2). */
export const MASS_HISTORY = deepFreeze({ intervalSec: 1, maxSamples: MAX_MASS_SAMPLES });
